/**
 * Tâche 1151 (Eric 2026-06-17) — Guide de vérification du domaine d'envoi.
 *
 * « The domain is not verified » : le refus le plus fréquent, et le message ne
 * dit pas où cliquer. Ce panneau interroge Resend avec la clé réellement
 * utilisée, dit où on en est, et donne les enregistrements DNS exacts à coller
 * — copiables, parce qu'une valeur DKIM retapée à la main est une valeur
 * fausse.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle, Circle, Copy, ShieldCheck } from "@phosphor-icons/react";

const API = process.env.REACT_APP_BACKEND_URL;

export const EmailDomainGuide = () => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(
        `${API}/api/system-notifications/email-domain`,
        { withCredentials: true }
      );
      setData(res.data);
    } catch {
      setData(null);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const recheck = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/api/system-notifications/email-domain/verify`,
        {},
        { withCredentials: true }
      );
      if (res.data?.state) setData(res.data.state);
      if (res.data?.ok && res.data.state?.verified)
        toast.success("Domaine vérifié — les envois peuvent partir");
      else
        toast.info(
          res.data?.reason ||
            "Pas encore vérifié : le DNS peut prendre quelques heures"
        );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Vérification impossible");
    } finally {
      setBusy(false);
    }
  };

  const copy = (value) => {
    navigator.clipboard?.writeText(value);
    toast.success("Copié");
  };

  // Tâche 1151 — Rien à afficher quand le domaine est vérifié (choix d'Eric) :
  // un bandeau vert permanent finit par être ignoré, et ce panneau n'existe
  // que pour débloquer un envoi qui ne part pas.
  if (!data || data.verified) return null;

  return (
    <section
      className="mb-6 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3"
      data-testid="email-domain-guide"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <ShieldCheck size={20} className="mt-0.5 shrink-0 text-amber-600" />
          <div className="text-sm">
            <p className="font-semibold" data-testid="email-domain-status">
              {data.key_present
                ? `Domaine ${data.domain} non vérifié — Resend refuse les envois`
                : "Aucune clé Resend configurée — aucun courriel ne peut partir"}
            </p>
            <p className="text-xs text-zinc-600 mt-0.5">
              Adresse d'expédition : <strong>{data.sender_email}</strong> · clé{" "}
              {data.key_source === "byok"
                ? "de cette organisation"
                : "partagée Knoria"}
              {data.error ? ` · ${data.error}` : ""}
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={recheck}
          disabled={busy}
          className="px-3 py-2 rounded-lg border border-zinc-300 bg-white text-sm text-zinc-700 hover:bg-zinc-50 disabled:opacity-50"
          data-testid="email-domain-recheck"
        >
          {busy ? "Vérification…" : "Revérifier"}
        </button>
      </div>

      <ol className="mt-3 space-y-1.5" data-testid="email-domain-steps">
        {(data.steps || []).map((s, i) => (
          <li
            key={s.title}
            className="flex items-start gap-2 text-sm"
            data-testid={`email-domain-step-${i}`}
          >
            {s.done ? (
              <CheckCircle size={16} className="mt-0.5 shrink-0 text-emerald-600" />
            ) : (
              <Circle size={16} className="mt-0.5 shrink-0 text-zinc-400" />
            )}
            <span>
              <span
                className={
                  s.done ? "text-zinc-500 line-through" : "font-medium text-zinc-900"
                }
              >
                {i + 1}. {s.title}
              </span>
              {s.detail && (
                <span className="block text-xs text-zinc-600">{s.detail}</span>
              )}
              {s.url && (
                <a
                  href={s.url}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-[#002FA7] underline"
                >
                  Ouvrir chez Resend
                </a>
              )}
            </span>
          </li>
        ))}
      </ol>

      {data.records?.length > 0 && (
        <div className="mt-3 overflow-x-auto rounded-lg border border-zinc-200 bg-white">
          <table className="w-full text-xs" data-testid="email-domain-records">
            <thead className="bg-zinc-50 text-zinc-600">
              <tr>
                <th className="text-left px-3 py-2">Type</th>
                <th className="text-left px-3 py-2">Nom</th>
                <th className="text-left px-3 py-2">Valeur</th>
                <th className="text-left px-3 py-2">État</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {data.records.map((r) => (
                <tr
                  key={`${r.type}-${r.name}`}
                  className="border-t border-zinc-100"
                  data-testid={`email-domain-record-${r.type}-${r.name}`}
                >
                  <td className="px-3 py-2 font-mono">{r.type}</td>
                  <td className="px-3 py-2 font-mono break-all">{r.name}</td>
                  <td className="px-3 py-2 font-mono break-all max-w-md">
                    {r.value}
                  </td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2">
                    <button
                      type="button"
                      onClick={() => copy(r.value)}
                      className="inline-flex items-center gap-1 text-[#002FA7]"
                      data-testid={`email-domain-copy-${r.type}-${r.name}`}
                    >
                      <Copy size={14} />
                      Copier
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

export default EmailDomainGuide;
